import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["document", "documentWithoutMedia"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useContext } from 'react';
import { ReactRenderer } from '@atlaskit/renderer';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { useMediaClient } from '@atlaskit/media-client-react';
import { EgressErrorMessage } from '../..';
import { RendererContext } from '../../../context';
import { useEgressPermissionService } from '../../../utils/useEgressPermissionService';
import { isValidImageUrl } from '../../utils/useIsValidImageUrl';

// Keep in sync with platform/packages/forge/forge-react-types/src/components/adfrenderer/index.ts

var filterInvalidMediaUrls = function filterInvalidMediaUrls(appDomainName, egressServices, document) {
  var invalidUrls = new Set();
  var visitors = {
    media: function media(node) {
      var _node$attrs;
      var url = node === null || node === void 0 || (_node$attrs = node.attrs) === null || _node$attrs === void 0 ? void 0 : _node$attrs.url;
      if (url && !egressServices.some(function (service) {
        return isValidImageUrl(url, service, appDomainName);
      })) {
        invalidUrls.add(url);
        return _objectSpread(_objectSpread({}, node), {}, {
          attrs: _objectSpread(_objectSpread({}, node.attrs), {}, {
            // Remove the URL so it fails to load
            // We use a space because an empty string causes an infinite loading spinner
            url: ' '
          })
        });
      }
    }
  };
  var filteredDocument = traverse(document, visitors);
  return {
    filteredDocument: filteredDocument ? filteredDocument : document,
    invalidUrls: Array.from(invalidUrls)
  };
};
var useSupportsMedia = function useSupportsMedia() {
  try {
    useMediaClient();
    return true;
  } catch (e) {
    // Throws if no MediaClientProvider is found
    return false;
  }
};

/**
 * The AdfRenderer component provides a way to render a valid ADF document, using the same renderer that Atlassian uses internally to render ADF content in Confluence pages, Jira work items, and so on.
 * It allows you to replace node types that are unsupported in the context of a Forge app with replacement content, or remove them entirely.
 * See [Atlassian Document Format](/cloud/jira/platform/apis/document/structure/) for information on valid nodes.
 *
 * This component uses [@atlaskit/renderer](https://www.npmjs.com/package/@atlaskit/renderer) under the hood.
 *
 * Visit [Renderer editor](https://atlaskit.atlassian.com/examples/editor/renderer/basic) for a comprehensive list of different ADF document examples
 */
export var AdfRenderer = function AdfRenderer(props) {
  var supportsMedia = useSupportsMedia();
  var _useContext = useContext(RendererContext),
    egress = _useContext.egress,
    appDomainName = _useContext.appDomainName,
    forgeEnvironment = _useContext.forgeEnvironment;
  var imagesEgressService = useEgressPermissionService('IMAGES', egress);
  var mediaEgressService = useEgressPermissionService('MEDIA', egress);
  var egressServices = [imagesEgressService, mediaEgressService];
  var _ref = props.forgeDoc.props,
    document = _ref.document,
    documentWithoutMedia = _ref.documentWithoutMedia,
    adfRendererProps = _objectWithoutProperties(_ref, _excluded);

  // The client provides both the document with media and without media, as it doesn't know whether the MediaClientProvider is present
  // Then, in the parent product, we can decide whether to render the document with media or without media
  var doc = !supportsMedia && documentWithoutMedia ? documentWithoutMedia : document;
  var _filterInvalidMediaUr = filterInvalidMediaUrls(appDomainName, egressServices, doc),
    filteredDocument = _filterInvalidMediaUr.filteredDocument,
    invalidUrls = _filterInvalidMediaUr.invalidUrls;
  return /*#__PURE__*/React.createElement(React.Fragment, null, forgeEnvironment === 'DEVELOPMENT' && invalidUrls.map(function (url) {
    return /*#__PURE__*/React.createElement(EgressErrorMessage, {
      key: url,
      url: url
    });
  }), /*#__PURE__*/React.createElement(RendererActionsContext, null, /*#__PURE__*/React.createElement(ReactRenderer, _extends({}, adfRendererProps, {
    document: filteredDocument
  }))));
};